import { useState } from 'react'
import { FiShare2 } from 'react-icons/fi'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { CommentsOn } from '@domain/models/activity-comments'
import { PODCAST_FAVOURITE_TYPE } from '@domain/features/user-favourites/user-favourites-feature'
import {
  buildSpotifyEpisodeEmbedUrl,
  hasPreferredVimeoEmbedUrl,
  selectPreferredPodcastEmbedUrl,
} from '@domain/models/podcast'
import CommentsSection from '@presentation/components/comments/CommentsSection'
import NewsShareModal from '@presentation/components/news/NewsShareModal'
import FavouriteBookmarkButton from '@presentation/components/ui/FavouriteBookmarkButton'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import SpotifyEmbed from '@presentation/components/ui/SpotifyEmbed'
import { usePodcast } from '@presentation/hooks/usePodcast'
import { useScrollToTopOnRouteEnter } from '@presentation/hooks/useScrollToTopOnRouteEnter'
import { useUserFavourite } from '@presentation/hooks/useUserFavourite'
import SeoHead from '@presentation/seo/SeoHead'
import StructuredData from '@presentation/seo/StructuredData'
import { buildBreadcrumbList } from '@presentation/seo/StructuredDataBuilders'
import { sanitizeForMeta, toAbsoluteUrl } from '@presentation/seo/seo-constants'

function formatReleaseDate(value: Date | undefined): string {
  if (!value) {
    return 'Release date unavailable'
  }

  return value.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export default function PodcastDetailPage() {
  const location = useLocation()
  useScrollToTopOnRouteEnter({ scrollOnPop: true })
  const navigate = useNavigate()
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const { episodeId } = useParams<{ episodeId: string }>()
  const { episode, loading, error } = usePodcast(episodeId)
  const {
    isFavourited,
    loading: favouriteLoading,
    toggling: favouriteToggling,
    error: favouriteError,
    toggle,
  } = useUserFavourite(episode?.uid, PODCAST_FAVOURITE_TYPE)

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const navigateBackToPodcastList = () => {
    if (location.key !== 'default') {
      navigate(-1)
      return
    }

    navigate('/prof-bas-bloem-weekly', { replace: true })
  }

  if (loading) {
    return (
      <>
        <SeoHead
          title="Loading podcast episode | Parkinson's Insights"
          canonicalPath={episodeId ? `/prof-bas-bloem-weekly/${episodeId}` : '/prof-bas-bloem-weekly'}
        />
        <LoadingSpinner className="min-h-[50vh]" />
      </>
    )
  }

  if (!episode) {
    return (
      <>
        <SeoHead
          title="Podcast episode not found | Parkinson's Insights"
          canonicalPath={episodeId ? `/prof-bas-bloem-weekly/${episodeId}` : '/prof-bas-bloem-weekly'}
          noindex
        />
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
          <p className="text-lg font-semibold text-text-secondary">
            {error ?? 'Podcast episode not found'}
          </p>
          <Link
            to="/prof-bas-bloem-weekly"
            className="mt-4 text-sm font-semibold text-primary hover:text-primary-light"
          >
            &larr; Back to Prof Bas Bloem Weekly
          </Link>
        </div>
      </>
    )
  }

  const episodeDescription = sanitizeForMeta(
    episode.description || episode.name,
    170,
  )
  const canonicalPath = `/prof-bas-bloem-weekly/${episode.uid}`
  const breadcrumbSchema = buildBreadcrumbList([
    { name: 'Home', pathOrUrl: '/' },
    { name: 'Prof Bas Bloem Weekly', pathOrUrl: '/prof-bas-bloem-weekly' },
    { name: episode.name, pathOrUrl: canonicalPath },
  ])
  const podcastEpisodeSchema = {
    '@context': 'https://schema.org',
    '@type': 'PodcastEpisode',
    name: episode.name,
    description: episodeDescription,
    url: toAbsoluteUrl(canonicalPath),
    datePublished: episode.releaseDate?.toISOString(),
    image: episode.imageUrl ? [toAbsoluteUrl(episode.imageUrl)] : undefined,
    associatedMedia: episode.spotifyUrl
      ? {
          '@type': 'MediaObject',
          contentUrl: episode.spotifyUrl,
        }
      : undefined,
    partOfSeries: {
      '@type': 'PodcastSeries',
      name: 'Prof Bas Bloem Weekly',
      url: toAbsoluteUrl('/prof-bas-bloem-weekly'),
    },
  }
  const prefersVimeoEmbed = hasPreferredVimeoEmbedUrl(episode)
  const preferredEmbedUrl = selectPreferredPodcastEmbedUrl(episode)
  const spotifyEmbedUrl = buildSpotifyEpisodeEmbedUrl(episode.episodeId)
  const shareUrl = toAbsoluteUrl(canonicalPath)
  const shareImageUrl = episode.imageUrl ? toAbsoluteUrl(episode.imageUrl) : undefined

  return (
    <article className="bg-background">
      <SeoHead
        title={`${episode.name} | Prof Bas Bloem Weekly`}
        description={episodeDescription}
        canonicalPath={canonicalPath}
        imageUrl={episode.imageUrl}
      />
      <StructuredData data={[breadcrumbSchema, podcastEpisodeSchema]} />
      <div className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={navigateBackToPodcastList}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Prof Bas Bloem Weekly
          </button>
          <div className="ml-auto flex flex-col items-end gap-2 sm:flex-row sm:items-center">
            <FavouriteBookmarkButton
              isFavourited={isFavourited}
              onClick={() => {
                void toggle(openLoginModal)
              }}
              disabled={favouriteLoading || favouriteToggling}
              className="h-11 shrink-0 gap-2 bg-background! px-4 text-blue! hover:bg-blue/10!"
              label={isFavourited ? 'Favorited' : 'Add To Favourites'}
              ariaLabel={isFavourited ? 'Remove podcast from favourites' : 'Add podcast to favourites'}
            />
            <button
              type="button"
              onClick={() => {
                setIsShareModalOpen(true)
              }}
              aria-label="Share podcast"
              className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-blue bg-background px-4 text-blue transition-colors hover:bg-blue/10"
            >
              <FiShare2 className="h-5 w-5" aria-hidden />
              <span className="text-sm font-semibold">Share</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-5 lg:px-8">
        <SpotifyEmbed
          episodeId={prefersVimeoEmbed ? undefined : episode.episodeId}
          embedUrl={preferredEmbedUrl}
          title={episode.name}
          className="shadow-none"
          iframeClassName={prefersVimeoEmbed ? 'aspect-video h-auto' : ''}
        />
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold tracking-tight text-text-primary sm:text-4xl">
          {episode.name}
        </h1>

        <p className="mt-3 text-sm font-bold text-primary">
          {formatReleaseDate(episode.releaseDate)}
        </p>

        {favouriteError ? (
          <p className="mt-2 text-sm text-text-secondary">
            {favouriteError}
          </p>
        ) : null}

        {episode.description ? (
          <p className="mt-6 whitespace-pre-wrap text-text-primary leading-relaxed">
            {episode.description}
          </p>
        ) : null}

        {prefersVimeoEmbed ? (
          <div className="mt-10">
            <p className="mb-3 text-sm font-semibold text-text-secondary">
              Listen on Spotify
            </p>
            <SpotifyEmbed
              episodeId={episode.episodeId}
              embedUrl={spotifyEmbedUrl}
              title={`${episode.name} (Spotify)`}
              className="shadow-none"
            />
          </div>
        ) : null}

        <hr className="mt-10 border-border-light" />

        <CommentsSection
          targetId={episode.uid}
          commentsOn={CommentsOn.Podcast}
          onAuthRequired={openLoginModal}
        />
      </div>
      <NewsShareModal
        isOpen={isShareModalOpen}
        onClose={() => {
          setIsShareModalOpen(false)
        }}
        shareUrl={shareUrl}
        title={episode.name}
        description={episodeDescription}
        imageUrl={shareImageUrl}
      />
    </article>
  )
}
